import { createApi } from '@reduxjs/toolkit/query/react';
import { axiosBaseQuery } from './AxiosBaseQuery';
import { API_SERVER_URL } from '@/constants/Config';
import type { IStockData } from '@/common/interfaces/stocks.interface';

interface StockQueryParams {
  styleCode: string;
  colorCode: string;
}

interface IStock {
  color_code: string;
  color_name: string;
  stock_data: IStockData[];
}

interface IStockApiResponse {
  status: string;
  data?: IStock[];
  message?: string;
}

export const buildStocksRequestUrl = ({ styleCode, colorCode }: StockQueryParams): string =>
  `api/stocks?style_code=${encodeURIComponent(styleCode)}&color_code=${encodeURIComponent(
    colorCode
  )}`;

export const normalizeStockQueryResponse = (
  apiResponse: IStockApiResponse | undefined
): { status: string; data: IStock } | null => {
  if (!apiResponse || apiResponse.status === 'error' || !Array.isArray(apiResponse.data)) {
    return null;
  }

  const stockEntry = apiResponse.data[0];

  if (!stockEntry?.stock_data?.length) {
    return null;
  }

  return {
    status: apiResponse.status,
    data: stockEntry,
  };
};

type StockBaseQueryResult =
  | IStockApiResponse
  | {
      data?: unknown;
      error?: unknown;
    };

type StockBaseQueryFn = (args: {
  url: string;
  method: string;
}) => StockBaseQueryResult | Promise<StockBaseQueryResult>;

const resolveStockApiResponse = (result: StockBaseQueryResult): IStockApiResponse | undefined => {
  if ('error' in result && result.error) {
    return undefined;
  }

  if ('status' in result) {
    return result;
  }

  return result.data as IStockApiResponse | undefined;
};

export const executeGetStocksQuery = async (
  params: StockQueryParams,
  baseQuery: StockBaseQueryFn
): Promise<{ data: { status: string; data: IStock } | null } | { error: unknown }> => {
  const result = await baseQuery({
    url: buildStocksRequestUrl(params),
    method: 'GET',
  });

  if ('error' in result && result.error) {
    return { error: result.error };
  }

  return {
    data: normalizeStockQueryResponse(resolveStockApiResponse(result)),
  };
};

export const stockQuery = createApi({
  reducerPath: 'stockQuery',
  baseQuery: axiosBaseQuery({
    baseUrl: API_SERVER_URL,
  }),
  tagTypes: ['Stock'],
  endpoints: (build) => ({
    getStocks: build.query<{ status: string; data: IStock } | null, StockQueryParams>({
      queryFn: async (params, _api, _extraOptions, baseQuery) =>
        executeGetStocksQuery(params, async (args) => {
          const result = await baseQuery(args);
          return result as StockBaseQueryResult;
        }),
    }),
  }),
});

export const { useGetStocksQuery, useLazyGetStocksQuery } = stockQuery;
